/**
 * voice-v8 — ein PDF ansehen, ohne die App zu verlassen.
 *
 * ## Woher das kommt
 *
 * Ticket 2396, Martin: *„Statt die Datei innerhalb der nativen App zu
 * oeffnen, oeffnet sich direkt die Datei, und die native App tritt dabei
 * komplett in den Hintergrund/verschwindet."*
 *
 * Der Herunterladen-Knopf ist seit pre.3073 im nativen Fall weg. Die beiden
 * PDF-Karten im Chat blieben offen, weil ihr zweiter Knopf (`target="_blank"`)
 * die App genauso verlaesst — und ihnen die Knoepfe zu nehmen haette die
 * Rechnung UNERREICHBAR gemacht statt nur unbequem. Das hier ist der Weg,
 * der drinnen bleibt.
 *
 * ## Die Seiten zeichnet pdf.js, nicht mehr der Rahmen (28.09.2026)
 *
 * Martin am iPhone, ein PDF-Link aus dem Chat: *„Das PDF passt nicht in die
 * Breite. Es lässt sich seitlich verschieben und wird nicht als Ganzes
 * angezeigt."*
 *
 * Bis pre.3416 stand das PDF in einem `<iframe>`. Was darin erscheint, malt
 * die WebView selbst — und iOS malt ein PDF in einem Rahmen in
 * Originalgröße, ohne Einpassen (das kann nur die Vollbild-Ansicht von iOS,
 * nicht der Rahmen). Nachgemessen an einer Rechnung: A4 ist 595 Punkte
 * breit, also 595 CSS-Pixel. Das Telefon hat 390. Rund ein Drittel der
 * Seite lag rechts ausserhalb — genau Martins Bild. Von aussen laesst sich
 * das nicht einstellen; der Inhalt eines Rahmens gehoert der WebView.
 *
 * Darum zeichnet jetzt pdf.js (pdfjs-laden.ts) jede Seite selbst, in der
 * Breite, die die Ansicht hat, eine unter der anderen. Wie die WebView PDFs
 * behandelt, spielt dann keine Rolle mehr. Der Rahmen bleibt fuer Dateien,
 * die kein PDF sind (Bilder, Ton — die Ansicht zeigt seit Build 77 jede
 * eigene Datei), und als Rueckfall, wenn pdf.js scheitert.
 *
 * ## Nachbesserung nach der Pruefung (28.09.2026)
 *
 * - Nur am Geraet mit Fingerbedienung (`zeichnetSelbst`). Am Rechner zeigt
 *   der Browser das PDF wie bisher selbst, mit Suche und Drucken — Martins
 *   Befund betraf das Telefon.
 * - Ueber jedem Bild liegt der Text der Seite, durchsichtig (Textebene von
 *   pdf.js): VoiceOver liest ihn, IBAN und Rechnungsnummer lassen sich
 *   markieren und kopieren.
 * - Zoom in der Ansicht: zwei Finger oder die Knoepfe unten rechts. Die
 *   ganze Seite zoomen geht nicht — index.html sperrt das (Schutz beim
 *   Drehen und gegen Auto-Zoom), und die Tastatur-Messung in app.ts liest
 *   jeden Seiten-Zoom als offene Tastatur.
 * - Scheitert pdf.js, geht eine Zeile ins Server-Protokoll, und ueber dem
 *   Rueckfall-Rahmen steht, dass die Seiten nicht eingepasst sind. Vorher
 *   fiel die Ansicht STILL auf den alten Rahmen zurueck — also genau auf
 *   Martins Befund, ohne dass es jemand merkte.
 *
 * ## Was gemessen ist, und was nicht
 *
 * GEMESSEN (20.09.2026):
 * - Die Rechnungs-PDFs liegen auf dem EIGENEN Server
 *   (`voice.ssi.at/downloads/...`, ohne Sitzung 401). Gleiche Herkunft, also
 *   traegt der Abruf die Anmeldung mit.
 *
 * ⚠️ Der sichtbare Ausweg („Im Browser oeffnen") bleibt IMMER da. Scheitert
 *    auch der Rueckfall-Rahmen, meldet der sich nicht — ein leerer Rahmen
 *    wirft kein Ereignis. Wer nichts sieht, ist nicht gefangen.
 */

import { dateiNameAus, heutigeAdresse } from "./app-browser.js";
import { meldeSpur } from "./mikro-spur.js";
import {
  ladePdfJs,
  oeffneDokument,
  type PdfDokument,
  type PdfJs,
  type PdfLadeAuftrag,
  type PdfMass,
  type PdfSeite,
} from "./pdfjs-laden.js";
import { makeShareIcon, shareMedia } from "./share.js";
import { zeichen } from "./zeichen.js";

/**
 * Sieht diese Adresse nach einem PDF aus? (Ticket 2396, 20.09.2026, am
 * 28.09. aus app.ts hierher gezogen — die Ansicht fragt jetzt selbst.)
 *
 * ⚠️ Bewusst zurueckhaltend. Bei allem anderen bleibt der alte Weg. Lieber
 *    ein PDF zu wenig gezeichnet als eine Seite, die leer bleibt.
 */
export function istPdfAdresse(url: string): boolean {
  try {
    const ohneFrage = String(url).split(/[?#]/)[0] ?? "";
    return /\.pdf$/i.test(ohneFrage);
  } catch {
    return false;
  }
}

/**
 * Zeichnet die Ansicht die Seiten selbst? Ja an Geraeten mit
 * Fingerbedienung (Telefon, Tablet), nein am Rechner. (Nachbesserung
 * 28.09.2026)
 *
 * Der eingebettete Rahmen taugt genau dort nicht: iOS malt ein PDF darin in
 * Originalgroesse (Martins Befund), Android zeigt darin gar keins. Am
 * Rechner malt ihn der PDF-Betrachter des Browsers — eingepasst, mit Suche
 * und Drucken. Den zu ersetzen war nie verlangt.
 */
function zeichnetSelbst(): boolean {
  return typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
}

/** Mehr Bildpunkte zeichnet iOS auf keine Leinwand (4096 × 4096), mit Luft. */
const GROESSTE_LEINWAND = 16_000_000;

/**
 * Wie scharf eine Seite gezeichnet wird: ihre Breite in PDF-Punkten auf die
 * Breite, die sie auf dem Schirm hat, mal der Pixeldichte des Geraets.
 *
 * ⚠️ Die Dichte ist bei 3 gedeckelt (iPhone), die Flaeche bei der groessten
 *    Leinwand, die iOS zeichnet. Groesser gezoomt wird die Seite dann nur
 *    noch hochgerechnet, statt gar nicht zu erscheinen.
 */
export function seitenMassstab(natur: PdfMass, schirmBreite: number, dichte: number): number {
  const d = Math.min(Math.max(dichte || 1, 1), 3);
  const gewuenscht = (schirmBreite / natur.width) * d;
  return Math.min(gewuenscht, Math.sqrt(GROESSTE_LEINWAND / (natur.width * natur.height)));
}

/** Die Stufen der Zoom-Knoepfe. Mit zwei Fingern geht es stufenlos dazwischen. */
const ZOOM_STUFEN = [1, 1.5, 2, 3] as const;
const ZOOM_GROESST = Math.max(...ZOOM_STUFEN);

/** Die eine offene Ansicht — zwei uebereinander waeren zwei Besitzer. */
let offen: HTMLElement | null = null;
/** Was beim Schliessen mit weg muss (Hoerer, Bildadressen, Dokument). */
let aufraeumen: (() => void) | null = null;

/** Schliesst eine offene PDF-Ansicht. Ohne eine ist es ein No-op. */
export function schliessePdf(): void {
  aufraeumen?.();
  aufraeumen = null;
  offen?.remove();
  offen = null;
}

/** Eine Seite in der Ansicht: Bild, darueber ihr Text. */
interface Blatt {
  el: HTMLElement;
  bild: HTMLImageElement;
  nummer: number;
  /** Groesse der Seite in PDF-Punkten. */
  natur: PdfMass;
  /** So breit ist das Bild gezeichnet, in Bildpunkten (0 = noch gar nicht). */
  gezeichnet: number;
  /** Die blob:-Adresse des Bildes — wird beim Neuzeichnen und Schliessen frei. */
  adresse: string | null;
}

/**
 * Die Textebene haengt an einer Zahl: CSS-Pixel je PDF-Punkt. Sie steht als
 * Variable am Blatt, weil pdf.js Lage und Schriftgroesse jedes Wortes daraus
 * rechnet; die Breite selbst gibt der Stil vor (.pdf-schau-blatt).
 */
function setzeMass(b: Blatt, breite: number): void {
  b.el.style.setProperty("--total-scale-factor", String(breite / b.natur.width));
}

/** Soll die Seite neu gezeichnet werden, weil sie breiter steht als ihr Bild? */
function brauchtSchaerfe(b: Blatt, breite: number): boolean {
  if (b.gezeichnet === 0) return false; // das erste Zeichnen laeuft noch
  const soll = Math.floor(b.natur.width * seitenMassstab(b.natur, breite, window.devicePixelRatio));
  return soll > b.gezeichnet * 1.15;
}

/**
 * Eine Seite als Bild zeichnen, in der Breite, die ihr Blatt gerade hat.
 *
 * ⚠️ Bilder, keine stehenden Leinwaende: iOS gibt einer Seite nur einen
 *    begrenzten Leinwand-Speicher, ein langes PDF laege sonst ganz darin.
 *    EINE Leinwand je Zeichnen, als Bild abgelegt, sofort geleert.
 * ⚠️ clientWidth, nicht getBoundingClientRect: das zaehlte die Einblende-
 *    Bewegung der Ansicht mit (scale) und zeichnete die Seiten zu klein.
 */
async function zeichneBild(seite: PdfSeite, b: Blatt, lage: HTMLElement): Promise<void> {
  const breite = b.el.clientWidth || b.natur.width;
  const ansicht = seite.getViewport({
    scale: seitenMassstab(b.natur, breite, window.devicePixelRatio),
  });
  const leinwand = document.createElement("canvas");
  leinwand.width = Math.floor(ansicht.width);
  leinwand.height = Math.floor(ansicht.height);
  await seite.render({ canvas: leinwand, viewport: ansicht }).promise;
  const png = await new Promise<Blob | null>((fertig) => leinwand.toBlob(fertig, "image/png"));
  const gezeichnet = leinwand.width;
  leinwand.width = 0;
  leinwand.height = 0;
  if (!png) throw new Error(`Seite ${b.nummer} liess sich nicht ablegen`);
  // Inzwischen geschlossen: keine Adresse mehr anlegen, die niemand freigibt.
  if (offen !== lage) return;
  if (b.adresse) URL.revokeObjectURL(b.adresse);
  b.adresse = URL.createObjectURL(png);
  b.bild.src = b.adresse;
  b.gezeichnet = gezeichnet;
}

/** Eine Zeile ins Server-Protokoll. Ohne Adresse: sie traegt Rechnungsnummern. */
function melde(was: string, err: unknown): void {
  const grund = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  meldeSpur("pdf-schau", `${was} grund=${grund.replace(/\s+/g, " ").slice(0, 160)}`);
}

/**
 * Ein PDF in der App zeigen.
 *
 * @param roh    Adresse des PDFs (eine alte eigene wird auf die heutige gesetzt).
 * @param titel  Was oben steht. Leer = „PDF".
 */
export function oeffnePdf(roh: string, titel?: string): HTMLElement {
  schliessePdf();
  /*
   * 26.09.2026 (Ticket 2675): Rechnungskarten im Verlauf tragen noch die
   * alte Adresse v8.ssi.at. Der Rahmen lud sie, wurde umgeleitet und kam
   * ohne Anmeldung an — {"error":"auth-required"} statt der Rechnung.
   * Dieselbe Datei über die heutige Herkunft ist eine eigene Adresse.
   */
  const url = heutigeAdresse(roh, location.href);
  const name = titel || "PDF";

  const lage = document.createElement("div");
  lage.className = "pdf-schau";
  lage.setAttribute("role", "dialog");
  lage.setAttribute("aria-modal", "true");
  lage.setAttribute("aria-label", name);

  const kopf = document.createElement("div");
  kopf.className = "pdf-schau-kopf";

  // Titel und Ausweg uebereinander: so bleibt am Telefon neben „Teilen“
  // und dem Schliessen-Knopf Platz fuer einen lesbaren Titel.
  const text = document.createElement("div");
  text.className = "pdf-schau-text";
  const kopfTitel = document.createElement("span");
  kopfTitel.className = "pdf-schau-titel";
  kopfTitel.textContent = name;
  text.appendChild(kopfTitel);

  /*
   * ⚠️ DER AUSWEG STEHT IMMER DA, nicht erst wenn etwas schiefgeht. Ob der
   *    Rahmen etwas anzeigt, laesst sich von innen nicht feststellen: ein
   *    leerer Rahmen wirft kein Ereignis.
   */
  const raus = document.createElement("a");
  raus.className = "pdf-schau-raus";
  raus.href = url;
  raus.target = "_blank";
  raus.rel = "noopener noreferrer";
  // Ticket 2396 (Build 77): in der App ins Browser-Blatt, nicht noch einmal
  // in diese Ansicht (app-browser.ts leitet eigene Adressen sonst hierher).
  raus.dataset.appBrowser = "immer";
  raus.textContent = "Im Browser öffnen";
  text.appendChild(raus);
  kopf.appendChild(text);

  /*
   * Teilen (28.09.2026, Martin: „Einen WhatsApp-Knopf sehe ich nicht“).
   * Derselbe Weg wie jeder Teilen-Knopf der App (share.ts): am Telefon das
   * Teilen-Blatt des Systems mit der DATEI daran — darin WhatsApp, Mail,
   * „In Dateien sichern“.
   *
   * ⚠️ Zeichnet die Ansicht selbst, ist der Knopf erst frei, wenn die Datei
   *    da ist. iOS oeffnet das Teilen-Blatt nur kurz nach dem Tipp; musste
   *    der Knopf erst noch holen, ging danach nur die Adresse hinaus — und
   *    die oeffnet beim Empfaenger nur die Anmeldung.
   */
  let datei: Blob | null = null;
  const teilen = document.createElement("button");
  teilen.type = "button";
  teilen.className = "btn btn--ghost btn--sm pdf-schau-teilen";
  teilen.appendChild(makeShareIcon(16));
  teilen.appendChild(document.createTextNode("Teilen"));
  teilen.addEventListener("click", () => {
    void shareMedia({
      url,
      title: name,
      dateiname: dateiNameAus(new URL(url, location.href)) || `${name}.pdf`,
      alsDatei: true,
      ...(datei ? { datei } : {}),
    });
  });
  kopf.appendChild(teilen);

  const zu = document.createElement("button");
  zu.type = "button";
  zu.className = "pdf-schau-zu";
  zu.setAttribute("aria-label", "Schließen");
  /*
   * ⚠️ Kein Dingbat. Ein „✕" sieht auf jedem Geraet anders aus und laesst
   *    sich nicht einfaerben; der Vorrat in zeichen.ts hat das gezeichnete
   *    Kreuz seit dem Zeichen-Umbau.
   */
  zu.innerHTML = zeichen("schliessen", 20);
  zu.addEventListener("click", () => schliessePdf());
  kopf.appendChild(zu);

  lage.appendChild(kopf);

  const seiten = document.createElement("div");
  seiten.className = "pdf-schau-seiten";
  lage.appendChild(seiten);

  // Escape schliesst — wie jede andere Vollflaeche in dieser App.
  const taste = (e: KeyboardEvent): void => {
    if (e.key === "Escape") schliessePdf();
  };
  document.addEventListener("keydown", taste);
  const blaetter = new Map<Element, Blatt>();
  let auftrag: PdfLadeAuftrag | null = null;
  let beobachter: ResizeObserver | null = null;
  let nachUhr: ReturnType<typeof setTimeout> | undefined;
  aufraeumen = () => {
    document.removeEventListener("keydown", taste);
    beobachter?.disconnect();
    clearTimeout(nachUhr);
    for (const b of blaetter.values()) if (b.adresse) URL.revokeObjectURL(b.adresse);
    // Auch mitten im Oeffnen: gibt Dokument und Hilfsarbeiter frei.
    void auftrag?.destroy().catch(() => undefined);
  };

  document.body.appendChild(lage);
  offen = lage;

  /** Der Rahmen: die WebView zeigt die Datei selbst. */
  const rahmenStattSeiten = (hinweis?: string): void => {
    const rahmen = document.createElement("iframe");
    rahmen.className = "pdf-schau-rahmen";
    rahmen.src = url;
    rahmen.title = name;
    lage.querySelector(".pdf-schau-zoom")?.remove();
    if (!hinweis) {
      seiten.replaceWith(rahmen);
      return;
    }
    const zeile = document.createElement("p");
    zeile.className = "pdf-schau-hinweis";
    zeile.setAttribute("role", "status");
    zeile.textContent = hinweis;
    seiten.replaceWith(zeile, rahmen);
  };

  if (!istPdfAdresse(url) || !zeichnetSelbst()) {
    rahmenStattSeiten();
    return lage;
  }

  teilen.disabled = true;

  /*
   * Zoom (Nachbesserung 28.09.2026: „zoombar bleibt erlaubt“ — die Seite
   * selbst laesst sich in dieser App nicht zoomen, siehe Kopf). EIN
   * Besitzer: setzeZoom. Knoepfe und zwei Finger rufen nur ihn. Die Breite
   * macht der Stil aus --pdf-zoom; schaerfer gezeichnet wird ueber den
   * Beobachter unten, wenn eine Seite breiter steht als ihr Bild.
   */
  let zoom = 1;
  const zoomLeiste = document.createElement("div");
  zoomLeiste.className = "pdf-schau-zoom";
  zoomLeiste.setAttribute("role", "group");
  zoomLeiste.setAttribute("aria-label", "Größe");
  const zoomKnopf = (art: string, beschriftung: string): HTMLButtonElement => {
    const k = document.createElement("button");
    k.type = "button";
    k.className = "pdf-schau-zoom-knopf";
    k.setAttribute("aria-label", beschriftung);
    k.innerHTML = zeichen(art, 20);
    zoomLeiste.appendChild(k);
    return k;
  };
  const kleiner = zoomKnopf("minus", "Verkleinern");
  const groesser = zoomKnopf("plus", "Vergrößern");
  const setzeZoom = (wunsch: number, anker?: { x: number; y: number }): void => {
    const neu = Math.min(Math.max(wunsch, 1), ZOOM_GROESST);
    kleiner.disabled = neu <= 1;
    groesser.disabled = neu >= ZOOM_GROESST;
    if (Math.abs(neu - zoom) < 0.001) return;
    // Der Punkt unter den Fingern (sonst die Mitte) bleibt, wo er ist.
    const r = seiten.getBoundingClientRect();
    const ax = anker ? anker.x - r.left : seiten.clientWidth / 2;
    const ay = anker ? anker.y - r.top : seiten.clientHeight / 2;
    const faktor = neu / zoom;
    const links = (seiten.scrollLeft + ax) * faktor - ax;
    const oben = (seiten.scrollTop + ay) * faktor - ay;
    zoom = neu;
    seiten.style.setProperty("--pdf-zoom", String(neu));
    seiten.scrollLeft = links;
    seiten.scrollTop = oben;
  };
  kleiner.addEventListener("click", () =>
    setzeZoom([...ZOOM_STUFEN].reverse().find((s) => s < zoom - 0.01) ?? 1),
  );
  groesser.addEventListener("click", () =>
    setzeZoom(ZOOM_STUFEN.find((s) => s > zoom + 0.01) ?? ZOOM_GROESST),
  );
  setzeZoom(1);
  lage.appendChild(zoomLeiste);

  const abstand = (t: TouchList): number =>
    Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  let griff: { abstand: number; zoom: number } | null = null;
  seiten.addEventListener(
    "touchstart",
    (e) => {
      if (e.touches.length === 2) griff = { abstand: abstand(e.touches) || 1, zoom };
    },
    { passive: true },
  );
  seiten.addEventListener(
    "touchmove",
    (e) => {
      if (!griff || e.touches.length !== 2) return;
      e.preventDefault();
      const [a, b] = [e.touches[0], e.touches[1]];
      setzeZoom((griff.zoom * abstand(e.touches)) / griff.abstand, {
        x: (a.clientX + b.clientX) / 2,
        y: (a.clientY + b.clientY) / 2,
      });
    },
    { passive: false },
  );
  const loslassen = (e: TouchEvent): void => {
    if (e.touches.length < 2) griff = null;
  };
  seiten.addEventListener("touchend", loslassen);
  seiten.addEventListener("touchcancel", loslassen);

  const stand = document.createElement("p");
  stand.className = "pdf-schau-stand";
  stand.setAttribute("role", "status");
  stand.textContent = "PDF wird geladen …";
  seiten.appendChild(stand);

  let dokument: PdfDokument | null = null;
  let ersterDurchgang: Promise<void> = Promise.resolve();
  const nachzeichnen = new Set<Blatt>();
  /** Seiten, die breiter stehen als ihr Bild, schaerfer zeichnen — nacheinander. */
  const zeichneNach = async (): Promise<void> => {
    await ersterDurchgang.catch(() => undefined);
    for (const b of [...nachzeichnen]) {
      nachzeichnen.delete(b);
      if (offen !== lage || !dokument) return;
      try {
        const seite = await dokument.getPage(b.nummer);
        await zeichneBild(seite, b, lage);
        seite.cleanup();
      } catch (err) {
        if (offen === lage) melde("nachzeichnen-fehler", err);
      }
    }
  };
  // Drehen, Zoomen: die Textebene rechnet sofort um, das Bild etwas spaeter.
  beobachter = new ResizeObserver((eintraege) => {
    for (const e of eintraege) {
      const b = blaetter.get(e.target);
      if (!b) continue;
      setzeMass(b, e.contentRect.width);
      if (brauchtSchaerfe(b, e.contentRect.width)) nachzeichnen.add(b);
    }
    if (nachzeichnen.size === 0) return;
    clearTimeout(nachUhr);
    nachUhr = setTimeout(() => void zeichneNach(), 250);
  });

  /** Jede Seite: Blatt anlegen, Bild zeichnen, Text darueberlegen. */
  const zeichneSeiten = async (pdfjs: PdfJs, dok: PdfDokument): Promise<void> => {
    for (let nummer = 1; nummer <= dok.numPages; nummer++) {
      if (offen !== lage) return; // geschlossen, waehrend gezeichnet wurde
      const seite = await dok.getPage(nummer);
      const natur = seite.getViewport({ scale: 1 });
      const el = document.createElement("div");
      el.className = "pdf-schau-blatt";
      const bild = document.createElement("img");
      bild.className = "pdf-schau-seite";
      bild.alt = `Seite ${nummer} von ${dok.numPages}`;
      // Das Seitenverhaeltnis steht sofort, die Breite gibt der Stil vor.
      bild.width = Math.round(natur.width);
      bild.height = Math.round(natur.height);
      const ebene = document.createElement("div");
      ebene.className = "pdf-schau-textebene";
      el.append(bild, ebene);
      seiten.appendChild(el);
      const b: Blatt = { el, bild, nummer, natur, gezeichnet: 0, adresse: null };
      blaetter.set(el, b);
      setzeMass(b, el.clientWidth || natur.width);
      beobachter?.observe(el);
      await zeichneBild(seite, b, lage);
      // „wird geladen“ gilt nur, bis die erste Seite steht.
      if (nummer === 1) stand.remove();
      try {
        await new pdfjs.TextLayer({ textContentSource: seite.streamTextContent(), container: ebene, viewport: natur }).render();
      } catch (err) {
        // Ohne Text bleibt die Seite lesbar — nur nicht markierbar.
        if (offen === lage) melde("textebene-fehler", err);
      }
      seite.cleanup();
    }
  };

  void (async () => {
    try {
      const [pdfjs, geholt] = await Promise.all([
        ladePdfJs(),
        fetch(url, { credentials: "include" }).then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.blob();
        }),
      ]);
      if (offen !== lage) return;
      datei = geholt;
      teilen.disabled = false;
      const daten = new Uint8Array(await geholt.arrayBuffer());
      if (offen !== lage) return;
      auftrag = oeffneDokument(pdfjs, daten);
      dokument = await auftrag.promise;
      ersterDurchgang = zeichneSeiten(pdfjs, dokument);
      await ersterDurchgang;
    } catch (err) {
      if (offen !== lage) return; // geschlossen: kein Fehler, nur vorbei
      /*
       * ⚠️ Nicht still. Der Rahmen hier ist am Telefon genau Martins Befund
       *    (Originalgroesse). Er zeigt wenigstens etwas; die Zeile darueber
       *    sagt, dass es nicht eingepasst ist, und das Protokoll, warum.
       */
      melde("seiten-fehler", err);
      teilen.disabled = false;
      rahmenStattSeiten("Die Seiten ließen sich hier nicht einpassen. „Im Browser öffnen“ zeigt die ganze Datei.");
    }
  })();

  return lage;
}
